import type { Product } from 'app/features/recommend/interfaces/models';
import type { AxiosError } from 'axios';
import { useMemo } from 'react';
import useSWR, { mutate } from 'swr';
import type {
  GetRecommendedProductRequest,
  GetRecommendedProductResponse,
} from '~/api/aspida/@types';
import { getRecommendedProduct } from '~/api/recommendedProduct';
import { productAdapter } from '~/features/recommend/productAdapter';

export const getRecommendedProductKey = (params: GetRecommendedProductRequest | null) =>
  params
    ? ([
        'getRecommendedProduct',
        params.child_id,
        params.date,
        params.lang,
        params.regionCode,
        params.is_newbie,
      ] as const)
    : null;

export const useGetRecommendedProduct = (params: GetRecommendedProductRequest | null) => {
  const {
    data,
    error,
    isLoading,
    mutate: revalidate,
  } = useSWR<GetRecommendedProductResponse, AxiosError>(
    getRecommendedProductKey(params),
    () => getRecommendedProduct(params!),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  // dataが変わらない限りadapter変換を再実行しない
  const products: Product[] = useMemo(
    () => (data ? data.products.map((p) => productAdapter(p)) : []),
    [data],
  );

  return { data, products, error, isLoading, revalidate };
};

export const fetchRecommendedProduct = async (
  params: GetRecommendedProductRequest,
): Promise<GetRecommendedProductResponse | undefined> => {
  try {
    const key = getRecommendedProductKey(params)!;
    return await mutate<GetRecommendedProductResponse>(key, () => getRecommendedProduct(params), {
      revalidate: false,
    });
  } catch (e) {
    console.error('製品推奨取得エラー', e);
    return undefined;
  }
};
